import { type QueryFunctionContext, useQuery } from "@tanstack/react-query";
import { type PaginationParams, type BasePerformanceRequestFilterParams } from "features/Library/Common/models";
import { lineChartFactory } from "features/Library/Common/utils/performanceUtils";
import { type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import { CHART_PERIOD, type ChartPeriod } from "hooks/useChartPeriodMeasure";
import DataService from "features/Application/services/dataServices/typedDataService";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";
import { reportingColors } from "features/Library/Common/utils/constants";
import { type FetchPageResult } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { type MessageOverviewDetails } from "features/Reporting/types/content";

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

const { messages } = DataService;

type MessageLineChartKey = QueryFunctionContext<
  [key: string, filter: BasePerformanceRequestFilterParams, aggregation: ChartPeriod]
>;

type MessageEngagementKey = QueryFunctionContext<
  [key: string, filter: BasePerformanceRequestFilterParams, hasCreateAccount: boolean | undefined]
>;

const fetchMessageLineChart = async ({ queryKey, signal }: MessageLineChartKey) => {
  const [, filter, aggregation] = queryKey;
  const { data } = await messages.getMessageLineChart(filter, undefined, aggregation, signal);
  return lineChartFactory(data);
};

const fetchMessageEngagement = async ({ queryKey, signal }: MessageEngagementKey) => {
  const [, filter, hasCreateAccount] = queryKey;
  const { data } = await messages.getMessageEngagement(filter, undefined, signal);

  const barData: DataPoint[] = [
    {
      id: "sends",
      category: "Sends",
      value: data.MessagesSent,
      fillColor: reportingColors.blue,
    },
    {
      id: "clicks",
      category: "Clicks",
      value: data.MessagesClicked,
      fillColor: reportingColors.yellow,
    },
  ];

  const cards = [
    {
      statDescription: "Messages",
      stat: data.Messages,
      popupBody: "The total number of messages sent.",
    },
    {
      statDescription: "Avg. Send Size",
      stat: data.AverageSendSize,
      popupBody: "The average number of recipients per message send.",
    },
    {
      statDescription: "Click-Through Rate",
      stat: data.ClickThroughRate * 100,
      popupBody: "The percentage of messages clicked compared to the total number of messages sent.",
      isStatPercentage: true,
    },
  ];

  if (hasCreateAccount) {
    cards.unshift({
      statDescription: "Accounts",
      stat: data.Accounts,
      popupBody: "The total number of customers across all messages.",
    });
  }
  return {
    barData,
    cards,
  };
};

export const fetchMessagesOverview = async (
  filter: PerformanceWithAccountFilter,
  pagination: PaginationParams,
  signal?: AbortSignal,
): Promise<FetchPageResult<MessageOverviewDetails>> => {
  const response = await messages.getMessagesOverview(filter, signal, pagination);

  const responseWithKey = response.data.map((message) => ({ ...message, id: message.MessageId }));

  return {
    rows: responseWithKey,
    totalCount: Number(response.headers?.[countHeaderName]),
  };
};

export const useMessageLineChartQuery = (
  filter: PerformanceWithAccountFilter,
  aggregation: ChartPeriod,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ["MessageOverviewLineChart", filter, aggregation],
    queryFn: fetchMessageLineChart,
    enabled: enabled && aggregation !== CHART_PERIOD.UNSET && filter.type !== undefined,
  });
};

export const useMessageEngagementQuery = (
  filter: PerformanceWithAccountFilter,
  hasCreateAccount: boolean | undefined,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ["MessageOverviewEngagement", filter, hasCreateAccount],
    queryFn: fetchMessageEngagement,
    enabled: enabled && filter.type !== undefined,
  });
};
